import { useState } from "react";
import { useTranslation } from "react-i18next";
import { img } from "../../constants/assets";
import { postApi } from "../../services/apiService";

const industryKeys = [
  "telecomOperator",
  "mvno",
  "travelHospitality",
  "retail",
  "technology",
  "other",
];

const countryKeys = ["uae", "us", "uk", "india", "germany", "singapore"];
const featureIcons = ["contact1.svg", "contact2.svg", "contact3.svg"];

function Contact() {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });
  const features = t("contact.features", { returnObjects: true });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    const formData = new FormData(event.target);
    const payload = {
      fullName: formData.get("fullName")?.trim(),
      email: formData.get("email")?.trim(),
      industry: formData.get("industry"),
      country: formData.get("country"),
      description: formData.get("description")?.trim(),
    };

    setIsSubmitting(true);

    try {
      await postApi("/api/contact", payload);
      event.target.reset();
      setStatus({
        type: "success",
        message: t("contact.success"),
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("contact.error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className="contact-section py-50">
        <div className="container">
          <div className="contact-card">
            <h1 className="contact-title">{t("contact.title")}</h1>
            <p className="contact-desc">{t("contact.desc")}</p>

            <div className="contact-card-body">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="book-demo-form-grid">
                  <div className="book-demo-field">
                    <input
                      type="text"
                      name="fullName"
                      placeholder={t("contact.fullNamePlaceholder")}
                      aria-label={t("contact.fullNameAria")}
                      required
                    />
                  </div>

                  <div className="book-demo-field">
                    <input
                      type="email"
                      name="email"
                      placeholder={t("contact.emailPlaceholder")}
                      aria-label={t("common.email")}
                      required
                    />
                  </div>

                  <div className="book-demo-field">
                    <select name="industry" defaultValue="" aria-label="Industry" required>
                      <option value="" disabled>
                        {t("contact.selectIndustry")}
                      </option>
                      {industryKeys.map((key) => (
                        <option key={key} value={t(`contact.industries.${key}`)}>
                          {t(`contact.industries.${key}`)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="book-demo-field">
                    <select name="country" defaultValue="" aria-label="Country" required>
                      <option value="" disabled>
                        {t("contact.selectCountry")}
                      </option>
                      {countryKeys.map((key) => (
                        <option key={key} value={t(`contact.countries.${key}`)}>
                          {t(`contact.countries.${key}`)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="book-demo-field book-demo-field--message">
                  <textarea
                    name="description"
                    placeholder={t("contact.messagePlaceholder")}
                    rows={5}
                    aria-label={t("common.message")}
                    required
                  />
                </div>

                {status.message && (
                  <p
                    className={`contact-form-status contact-form-status--${status.type}`}
                    role="alert"
                  >
                    {status.message}
                  </p>
                )}

                <button
                  type="submit"
                  className="contact-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("contact.sending") : t("contact.sendMessage")}
                  <img src={img("msg.svg")} alt="" aria-hidden="true" />
                </button>
              </form>

              <div className="contact-image">
                <img src={img("contactcall.svg")} alt={t("contact.imageAlt")} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-features-section py-50">
        <div className="container">
          <div className="contact-features">
            {features.map((feature, index) => (
              <article className="contact-feature-card" key={feature.title}>
                <div className="contact-feature-icon">
                  <img src={img(featureIcons[index])} alt="" aria-hidden="true" />
                </div>
                <h3 className="contact-feature-title">{feature.title}</h3>
                <p className="contact-feature-text">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;
